import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import useLogActivityAccess from '../hooks/useLogActivityAccess';

const CAT_COLORS = {
  'Clima':         { from: '#1e90ff', to: '#0052cc' },
  'Sorte':         { from: '#f39c12', to: '#e67e22' },
  'Dinheiro':      { from: '#27ae60', to: '#16a085' },
  'Trabalho':      { from: '#8e44ad', to: '#6c3483' },
  'Comunicação':   { from: '#e74c3c', to: '#c0392b' },
  'Relacionamentos':{ from: '#e91e8c', to: '#9c27b0' },
  'Tempo':         { from: '#00bcd4', to: '#0097a7' },
  'Comportamento': { from: '#ff5722', to: '#e64a19' },
  'Decisões':      { from: '#607d8b', to: '#455a64' },
  'Coragem':       { from: '#ff9800', to: '#f57c00' },
  'Sucesso':       { from: '#4caf50', to: '#388e3c' },
  'Aprendizado':   { from: '#3f51b5', to: '#283593' },
  'Comida':        { from: '#795548', to: '#5d4037' },
  'Vida':          { from: '#009688', to: '#00695c' },
};

const defaultColor = { from: '#1e90ff', to: '#0052cc' };

const NIVEL_STYLE = {
  beginner:     { bg: 'rgba(46,204,113,0.2)',  text: '#7effc0', label: 'Iniciante' },
  intermediate: { bg: 'rgba(241,196,15,0.2)',  text: '#ffe87e', label: 'Intermediário' },
  advanced:     { bg: 'rgba(231,76,60,0.2)',   text: '#ff9e91', label: 'Avançado' },
};

const S = {
  page: { minHeight: 'calc(100vh - 66px)', background: 'linear-gradient(160deg,#0a1628 0%,#1a2a4a 100%)', padding: '32px 20px', fontFamily: 'Poppins,sans-serif' },
  inner: { maxWidth: 1080, margin: '0 auto' },
  title: { color: '#fff', fontSize: 'clamp(1.5rem,4vw,2.2rem)', fontWeight: 800, textAlign: 'center', marginBottom: 6 },
  subtitle: { color: 'rgba(255,255,255,0.55)', textAlign: 'center', fontSize: '0.95rem', marginBottom: 28 },
  controls: { display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 24 },
  search: { padding: '11px 18px', borderRadius: 30, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.95rem', outline: 'none', fontFamily: 'Poppins,sans-serif', width: 280 },
  select: { padding: '11px 18px', borderRadius: 30, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.92rem', outline: 'none', fontFamily: 'Poppins,sans-serif', cursor: 'pointer' },
  tabs: { display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24, flexWrap: 'wrap' },
  tab: (a) => ({ padding: '8px 20px', borderRadius: 30, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'Poppins,sans-serif', background: a ? '#1e90ff' : 'rgba(255,255,255,0.1)', color: a ? '#fff' : 'rgba(255,255,255,0.65)', transition: 'all 0.2s' }),
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 20 },
  card: { borderRadius: 18, overflow: 'hidden', boxShadow: '0 6px 24px rgba(0,0,0,0.25)', transition: 'transform 0.2s', cursor: 'pointer' },
  cardHead: (cat) => {
    const c = CAT_COLORS[cat] || defaultColor;
    return { background: `linear-gradient(135deg,${c.from},${c.to})`, padding: '18px 20px' };
  },
  idiomText: { color: '#fff', fontWeight: 800, fontSize: '1.15rem', margin: 0 },
  ptBadge: { background: 'rgba(0,0,0,0.25)', color: '#fff', borderRadius: 10, padding: '4px 10px', fontSize: '0.76rem', fontWeight: 700, display: 'inline-block', marginTop: 8 },
  cardBody: { background: 'rgba(255,255,255,0.05)', padding: '16px 20px' },
  sigLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 },
  sigText: { color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', marginBottom: 14, fontStyle: 'italic' },
  exEn: { color: 'rgba(255,255,255,0.55)', fontSize: '0.83rem', fontStyle: 'italic', marginBottom: 4 },
  exPt: { color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', fontStyle: 'italic', marginBottom: 10 },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 },
  catTag: (cat) => {
    const c = CAT_COLORS[cat] || defaultColor;
    return { background: `rgba(${hexToRgb(c.from)},0.2)`, color: '#fff', borderRadius: 10, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700 };
  },
  audioBtn: { background: 'rgba(30,144,255,0.3)', border: 'none', borderRadius: 8, padding: '5px 11px', cursor: 'pointer', color: '#7ecfff', fontSize: '0.85rem' },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: 60, fontSize: '1.1rem' },
};

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `${r},${g},${b}`;
}

function playTTS(text, e) {
  e.stopPropagation();
  fetch('/synt/text-to-speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, languageCode: 'en-US', voiceName: 'en-US-Neural2-J', pitch: 0 }),
  }).then(r => r.blob()).then(blob => new Audio(URL.createObjectURL(blob)).play()).catch(() => {});
}

function IdiomCard({ item }) {
  const [open, setOpen] = useState(false);
  const nv = NIVEL_STYLE[item.nivel] || NIVEL_STYLE.intermediate;

  return (
    <div
      style={S.card}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
      onClick={() => setOpen(o => !o)}
    >
      <div style={S.cardHead(item.categoria)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <p style={S.idiomText}>"{item.idiom}"</p>
            {item.equivalente_pt && (
              <span style={S.ptBadge}>🇧🇷 {item.equivalente_pt}</span>
            )}
          </div>
          <button style={{ ...S.audioBtn, marginLeft: 10, flexShrink: 0 }} onClick={e => playTTS(item.idiom, e)}>🔊</button>
        </div>
      </div>

      <div style={S.cardBody}>
        <p style={S.sigLabel}>Significado</p>
        <p style={S.sigText}>{item.significado_en}</p>

        {open && item.exemplo_en && (
          <>
            <p style={S.sigLabel}>Exemplo</p>
            <p style={S.exEn}>"{item.exemplo_en}"</p>
            {item.exemplo_pt && <p style={S.exPt}>"{item.exemplo_pt}"</p>}
          </>
        )}

        <div style={S.footer}>
          <span style={S.catTag(item.categoria)}>{item.categoria || '—'}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ background: nv.bg, color: nv.text, borderRadius: 10, padding: '2px 9px', fontSize: '0.7rem', fontWeight: 700 }}>
              {nv.label}
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem' }}>
              {open ? '▲ fechar' : '▼ exemplo'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Idioms() {
  useLogActivityAccess('idioms');
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [nivel, setNivel] = useState('all');
  const [categoria, setCategoria] = useState('all');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const cache = useRef({});

  useEffect(() => {
    api.get('/idioms/categorias').then(r => setCategorias(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const key = `${categoria}|${nivel}`;
    if (cache.current[key]) { setItems(cache.current[key]); setLoading(false); return; }
    setLoading(true);
    const params = [];
    if (categoria !== 'all') params.push(`categoria=${encodeURIComponent(categoria)}`);
    if (nivel !== 'all') params.push(`nivel=${nivel}`);
    const url = '/idioms' + (params.length ? '?' + params.join('&') : '');
    api.get(url)
      .then(r => { cache.current[key] = r.data; setItems(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [categoria, nivel]);

  const filtrados = items.filter(item =>
    item.idiom.toLowerCase().includes(busca.toLowerCase()) ||
    (item.equivalente_pt || '').toLowerCase().includes(busca.toLowerCase()) ||
    (item.significado_en || '').toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.title}>💬 Idioms em Inglês</h1>
        <p style={S.subtitle}>Expressões idiomáticas com equivalentes em português do Brasil — clique no card para ver o exemplo</p>

        <div style={S.controls}>
          <input
            style={S.search}
            placeholder="🔍 Buscar idiom ou equivalente em PT..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
          <select style={S.select} value={categoria} onChange={e => { setCategoria(e.target.value); setBusca(''); }}>
            <option value="all">Todas as categorias</option>
            {categorias.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        <div style={S.tabs}>
          {[['all','Todos'],['beginner','Iniciante'],['intermediate','Intermediário'],['advanced','Avançado']].map(([k,l]) => (
            <button key={k} style={S.tab(nivel===k)} onClick={() => { setNivel(k); setBusca(''); }}>{l}</button>
          ))}
        </div>

        {loading ? (
          <p style={S.empty}><i className="fas fa-spinner fa-spin" /> Carregando...</p>
        ) : filtrados.length === 0 ? (
          <p style={S.empty}>Nenhum idiom encontrado.</p>
        ) : (
          <>
            <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', fontSize: '0.82rem', marginBottom: 20 }}>
              {filtrados.length} idiom{filtrados.length !== 1 ? 's' : ''} encontrado{filtrados.length !== 1 ? 's' : ''}
            </p>
            <div style={S.grid}>
              {filtrados.map(item => <IdiomCard key={item.id} item={item} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
